/**
 * Enterprise-grade KPI Progress Calculation Utility
 * 
 * Handles:
 * - Multiple measurement modes (numeric, percentage, rating, binary)
 * - Measurement direction (higher_is_better, lower_is_better)
 * - Input validation and sanitization
 * - Rating scale conversions
 */

import { 
  MeasurementDirection, 
  MeasurementMode, 
  RatingScaleType, 
  RatingOption,
  RATING_SCALES 
} from '@/types/kpi'

export interface ProgressResult {
  progress: number
  status: 'achieved' | 'on-track' | 'at-risk' | 'off-track' | 'not-started'
  display: string
  rawRatio: number
}

export interface ValidationResult {
  isValid: boolean
  value: number | null
  error: string | null
}

/**
 * Validate and parse a measurement value based on KPI configuration
 */
export function validateAndParseValue(
  rawValue: string | number | null | undefined,
  mode: MeasurementMode = 'numeric',
  minValue?: number | null,
  maxValue?: number | null,
  ratingScale?: RatingScaleType,
  ratingOptions?: RatingOption[]
): ValidationResult {
  // Handle null/empty
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return { isValid: false, value: null, error: 'Value is required' }
  }

  let stringValue = String(rawValue).trim()

  // Remove common formatting
  stringValue = stringValue.replace(/,/g, '').replace(/\s/g, '')

  // Handle percentage signs
  if (stringValue.endsWith('%')) {
    stringValue = stringValue.slice(0, -1)
  }

  // Handle currency symbols
  for (const symbol of ['R', '$', '€', '£', 'ZAR']) {
    stringValue = stringValue.replace(symbol, '')
  }

  // Handle rating mode
  if (mode === 'rating') {
    return parseRatingValue(stringValue, ratingScale, ratingOptions)
  }

  // Handle binary mode
  if (mode === 'binary') {
    return parseBinaryValue(stringValue)
  }

  // Try to convert to number
  const value = parseFloat(stringValue)
  if (isNaN(value) || !isFinite(value)) {
    return { 
      isValid: false, 
      value: null, 
      error: `Invalid number: '${rawValue}'. Please enter a valid numeric value.` 
    }
  }

  // Mode-specific validation
  if (mode === 'percentage') {
    if (value < 0) {
      return { isValid: false, value: null, error: 'Percentage cannot be negative' }
    }
    if (value > 100) {
      return { isValid: false, value: null, error: `Percentage cannot exceed 100% (got ${value}%)` }
    }
  }

  // Min/max validation
  if (minValue !== null && minValue !== undefined && value < minValue) {
    return { isValid: false, value: null, error: `Value ${value} is below minimum ${minValue}` }
  }
  if (maxValue !== null && maxValue !== undefined && value > maxValue) {
    return { isValid: false, value: null, error: `Value ${value} exceeds maximum ${maxValue}` }
  }

  return { isValid: true, value, error: null }
}

function parseRatingValue(
  rawValue: string,
  ratingScale?: RatingScaleType,
  ratingOptions?: RatingOption[]
): ValidationResult {
  const options = ratingScale === 'custom' && ratingOptions 
    ? ratingOptions 
    : RATING_SCALES[ratingScale || 'three_point']

  const rawLower = rawValue.toLowerCase()

  // Try exact match
  for (const option of options) {
    if (option.label.toLowerCase() === rawLower) {
      return { isValid: true, value: option.value, error: null }
    }
  }

  // Try partial match
  for (const option of options) {
    if (option.label.toLowerCase().includes(rawLower)) {
      return { isValid: true, value: option.value, error: null }
    }
  }

  // Try as numeric (index or direct value)
  const numericVal = parseFloat(rawValue)
  if (!isNaN(numericVal)) {
    // Check if it's a 1-based index
    if (numericVal >= 1 && numericVal <= options.length) {
      return { isValid: true, value: options[Math.floor(numericVal) - 1].value, error: null }
    }
    // Check if it's a direct progress value
    for (const option of options) {
      if (option.value === numericVal) {
        return { isValid: true, value: numericVal, error: null }
      }
    }
  }

  const validLabels = options.map(opt => opt.label).join(', ')
  return { isValid: false, value: null, error: `Invalid rating. Valid options: ${validLabels}` }
}

function parseBinaryValue(rawValue: string): ValidationResult {
  const rawLower = rawValue.toLowerCase().trim()

  const achievedValues = ['achieved', 'yes', 'true', '1', 'complete', 'done', 'pass']
  const notAchievedValues = ['not achieved', 'not-achieved', 'no', 'false', '0', 'incomplete', 'fail']

  if (achievedValues.includes(rawLower)) {
    return { isValid: true, value: 100, error: null }
  }
  if (notAchievedValues.includes(rawLower)) {
    return { isValid: true, value: 0, error: null }
  }

  return { isValid: false, value: null, error: `Invalid binary value: '${rawValue}'. Use 'Achieved' or 'Not Achieved'` }
}

/**
 * Calculate progress percentage based on measurement configuration
 */
export function calculateProgress(
  currentValue: number | null | undefined,
  targetValue: number,
  direction: MeasurementDirection = 'higher_is_better',
  mode: MeasurementMode = 'numeric',
  ratingScale?: RatingScaleType
): ProgressResult {
  const result: ProgressResult = {
    progress: 0,
    status: 'not-started',
    display: 'Not started',
    rawRatio: 0
  }

  // Handle no current value
  if (currentValue === null || currentValue === undefined) {
    return result
  }

  // Handle rating mode - current_value is already the progress percentage
  if (mode === 'rating') {
    result.progress = Math.min(Math.max(Math.floor(currentValue), 0), 100)
    result.rawRatio = currentValue / 100
    result.status = getStatusFromProgress(result.progress)
    result.display = `${result.progress}%`
    return result
  }

  // Handle binary mode
  if (mode === 'binary') {
    result.progress = currentValue >= 100 ? 100 : 0
    result.rawRatio = currentValue >= 100 ? 1 : 0
    result.status = currentValue >= 100 ? 'achieved' : 'off-track'
    result.display = currentValue >= 100 ? 'Achieved' : 'Not Achieved'
    return result
  }

  // Handle numeric and percentage modes
  if (targetValue === 0) {
    if (currentValue === 0) {
      result.progress = 100
      result.status = 'achieved'
    } else {
      result.progress = direction === 'higher_is_better' ? 0 : 100
    }
    result.display = `${result.progress}%`
    return result
  }

  // Calculate raw ratio based on direction
  let rawRatio: number
  if (direction === 'higher_is_better') {
    rawRatio = currentValue / targetValue
  } else {
    // Lower is better: target 5 days, current 4 days = 125%
    // If currentValue is 0, treat as "not started" - user hasn't recorded a measurement yet
    // (0 days turnaround doesn't mean instant completion, it means no data)
    if (currentValue === 0) {
      result.progress = 0
      result.status = 'not-started'
      result.display = 'Not started'
      result.rawRatio = 0
      return result
    }
    rawRatio = targetValue / currentValue
  }

  result.rawRatio = rawRatio

  // Convert to percentage and clip to 0-100
  const progressPct = rawRatio * 100
  result.progress = Math.min(Math.max(Math.floor(progressPct), 0), 100)
  result.status = getStatusFromProgress(result.progress)
  result.display = `${result.progress}%`

  return result
}

function getStatusFromProgress(progress: number): ProgressResult['status'] {
  if (progress >= 100) return 'achieved'
  if (progress >= 70) return 'on-track'
  if (progress >= 40) return 'at-risk'
  return 'off-track'
}

/**
 * Get human-readable display of current progress
 */
export function getProgressDisplay(
  currentValue: number | null | undefined,
  targetValue: number,
  unit: string,
  direction: MeasurementDirection = 'higher_is_better',
  mode: MeasurementMode = 'numeric',
  ratingScale?: RatingScaleType,
  ratingOptions?: RatingOption[]
): string {
  if (currentValue === null || currentValue === undefined) {
    return `- / ${targetValue} ${unit}`
  }

  if (mode === 'binary') {
    return currentValue >= 100 ? 'Achieved' : 'Not Achieved'
  }

  if (mode === 'rating') {
    const options = ratingScale === 'custom' && ratingOptions 
      ? ratingOptions 
      : RATING_SCALES[ratingScale || 'three_point']
    
    for (const opt of options) {
      if (opt.value === currentValue) {
        return opt.label
      }
    }
    return `${currentValue}%`
  }

  // Numeric and percentage modes
  const directionHint = direction === 'lower_is_better' ? ' ↓' : ''
  return `${currentValue} / ${targetValue} ${unit}${directionHint}`
}

/**
 * Get available rating options for a rating-mode KPI
 */
export function getRatingOptions(
  ratingScale?: RatingScaleType,
  customOptions?: RatingOption[]
): RatingOption[] {
  if (ratingScale === 'custom' && customOptions) {
    return customOptions
  }
  return RATING_SCALES[ratingScale || 'three_point']
}

/**
 * Get status color based on measurement mode and current status
 */
export function getStatusColor(
  status: ProgressResult['status'],
  mode: MeasurementMode = 'numeric',
  ratingScale?: RatingScaleType,
  currentValue?: number
): string {
  // For rating mode, use the rating option's color if available
  if (mode === 'rating' && currentValue !== undefined) {
    const options = RATING_SCALES[ratingScale || 'three_point']
    for (const opt of options) {
      if (opt.value === currentValue && opt.color) {
        return opt.color
      }
    }
  }

  // Standard status colors
  switch (status) {
    case 'achieved': return '#10B981' // Green
    case 'on-track': return '#10B981' // Green
    case 'at-risk': return '#F59E0B'  // Amber
    case 'off-track': return '#EF4444' // Red
    default: return '#6B7280' // Gray
  }
}
